// Tracks in-app navigation (React Router pathname changes) and posts
// browsing events to the backend, which forwards them to ClickHouse.
// Web-only: we cannot see browser-wide history. The Electron agent will
// hook webContents.did-navigate later for full coverage.

import { timeApi } from './api'

let lastUrl = ''
let lastEnter = 0
let initialized = false

function send(url: string, durationMs: number) {
  const u = new URL(url, window.location.origin)
  // Strip query strings that contain tokens / IDs we don't need.
  // Keep pathname only — that's what the admin dashboards group on.
  const cleanedUrl = `${u.origin}${u.pathname}`
  const title = document.title || ''
  timeApi.browsingEvent({
    url: cleanedUrl,
    title: title.slice(0, 200),
    durationMs,
    domain: u.hostname,
    source: 'WEB_INAPP',
  }).catch(() => {})
}

function flushPrevious(now: number) {
  if (lastUrl) {
    send(lastUrl, now - lastEnter)
  }
}

function cleanedHref(href: string): string {
  try {
    const u = new URL(href, window.location.origin)
    return `${u.origin}${u.pathname}`
  } catch {
    return href
  }
}

function onLocationChange() {
  const now = Date.now()
  // Dedupe by cleaned URL — React Router calls replaceState repeatedly
  // when the route's loader resolves, the modal-open `?modal=…` query
  // bounces, deep-link `?msg=` strips, etc. Each of those keeps the
  // pathname identical but flipped window.location.href, and the old
  // version of this tracker faithfully posted a browsing event for
  // every flap. On a busy session that alone could blow past the
  // server's 300/min global rate limit and surface as a 429 in the
  // console (POST /api/time/browsing-event 429). Compare on the same
  // shape `send()` actually transmits — origin + pathname — so a
  // path-stable URL with a moving query is a no-op.
  const nextHref = window.location.href
  if (cleanedHref(nextHref) === cleanedHref(lastUrl)) {
    // Same logical path — do nothing. lastEnter keeps accruing dwell
    // for the current page; flushPrevious will land it as one event
    // when the user actually navigates somewhere else.
    return
  }
  flushPrevious(now)
  lastUrl = nextHref
  lastEnter = now
}

export function startNavigationTracker() {
  if (initialized) return
  initialized = true
  lastUrl = window.location.href
  lastEnter = Date.now()

  // History API doesn't fire popstate for pushState/replaceState — patch
  // them so we get every SPA navigation.
  const origPush = history.pushState
  const origReplace = history.replaceState
  history.pushState = function (...args: any[]) {
    const ret = origPush.apply(this, args as any)
    queueMicrotask(onLocationChange)
    return ret
  }
  history.replaceState = function (...args: any[]) {
    const ret = origReplace.apply(this, args as any)
    queueMicrotask(onLocationChange)
    return ret
  }
  window.addEventListener('popstate', onLocationChange)
  window.addEventListener('beforeunload', () => flushPrevious(Date.now()))
}

// External pages the user opens in a new tab — call this from any
// component that triggers a window.open() on an outside link, so
// the browsing history covers more than internal routes.
export function reportExternalOpen(url: string, title?: string) {
  try {
    const u = new URL(url, window.location.origin)
    timeApi.browsingEvent({
      url: u.toString(),
      title: title?.slice(0, 200),
      durationMs: 0,
      domain: u.hostname,
      source: 'WEB_INAPP',
    }).catch(() => {})
  } catch {
    // ignore malformed urls
  }
}
